import {
  faCaretDown,
  faCaretUp,
  faCheckCircle,
  faFile,
  faCommentDots,
  faCircleXmark,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { FC } from 'react'
import { useState } from 'react'
import styled from 'styled-components'

import H6 from '@/components/H6'
import Lightbox, { getFile } from '@/components/Lightbox'
import TertiaryAction from '@/components/TertiaryAction'
import type { DocumentRequest as DocumentRequestT } from '@/lib/apollo/query/get-document-requests'
import { parseDate, toDateLabel } from '@/utils/date'
import { useUserTracking } from '@/utils/tracking'

import ExpandedDocumentRequest from './Expanded'

interface Props {
  customerId: string
  applicationId: string
  subjectId: string
  documentRequest: DocumentRequestT
  subjectName: string
}

const Container = styled.div<{ $isCompleted: boolean }>`
  border: 2px solid ${props => (props.$isCompleted ? '#f2f1f1' : '#ED941E')};
  display: grid;
  gap: 10px;
  font-family: Montserrat;
`

const CollapsedContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr min-content;
  align-items: center;
  padding: 15px;
  gap: 22px;

  &:hover {
    background: #f2f1f1;
    cursor: pointer;
  }
`

const ExpandedContainer = styled.div`
  padding: 10px;
`

const HeaderLeftContainer = styled.div`
  display: grid;
  gap: 10px;
`

const DocumentAndAdditionalContextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: #f2f2f2;
  justify-self: center;
  align-items: flex-start;
  padding: 14px;
  gap: 12px;
  margin-bottom: 36px;
`

const AdditionalContextHeadingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`

const DocChaseInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 28px;
`

const DocChaseInfoItem = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  justify-content: center;
`

const DocumentInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-top: 18px;
  width: 100%;
`

const DocumentInfo = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const AttachedDocumentIconWrapper = styled.div`
  position: relative;
`

const StyledCheckmarkIcon = styled(FontAwesomeIcon)`
  position: absolute;
  right: -6px;
  bottom: -4px;
`

const CreatedDateWrapper = styled.div`
  grid-column: 1 / -1;
  color: #4f4f4f;
  font-size: 12px;
  font-style: italic;
  font-weight: 400;
  line-height: normal;
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: #c9c9c9;
`

const CommentWrapper = styled.div`
  border-radius: 4px;
  background: #fff;
  min-height: 108px;
  width: 100%;
  padding: 18px;
`

const CommentIconWrapper = styled.div`
  border-radius: 50%;
  background-color: #2f80ed;
  height: 27px;
  width: 27px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const CommentHeading = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  gap: 18px;
  align-items: center;
`

const CommentContent = styled.div`
  margin-top: 6px;
  margin-left: 44px;
`

const DocumentRequest: FC<Props> = ({
  customerId,
  applicationId,
  subjectId,
  documentRequest,
  subjectName,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState<number>(0)
  const [isLightboxOpen, setIsLightBoxOpen] = useState<boolean>(false)

  const images = documentRequest.documentsUploaded?.map(url => ({ fileName: url })) ?? []

  const { trackUserActivity } = useUserTracking()

  const handleOpenDocument = (index: number) => {
    setCurrentDocumentIndex(index)
    setIsLightBoxOpen(true)

    if (images.length > 0) {
      trackUserActivity('View document', {
        subjectId,
        customerId,
        fileName: getFile(images[index]),
      })
    }
  }

  const hasUploadedDocuments =
    documentRequest.documentsUploaded && documentRequest.documentsUploaded.length > 0

  return (
    <Container $isCompleted={documentRequest.completed}>
      {isLightboxOpen && documentRequest.documentsUploaded && (
        <Lightbox
          images={images}
          onClose={() => setIsLightBoxOpen(false)}
          startIndex={currentDocumentIndex}
          customerId={customerId}
          subjectId={subjectId}
        />
      )}
      <CollapsedContainer onClick={() => setIsExpanded(!isExpanded)}>
        <HeaderLeftContainer>
          <div>
            <strong>{documentRequest.documentType}</strong>: requested by{' '}
            <strong>{documentRequest.author}</strong>
          </div>
        </HeaderLeftContainer>
        <div>
          {isExpanded ? (
            <FontAwesomeIcon icon={faCaretUp} size="2x" />
          ) : (
            <FontAwesomeIcon icon={faCaretDown} size="2x" />
          )}
        </div>
        {documentRequest.completed &&
          (hasUploadedDocuments || documentRequest.additionalContext) && (
            <DocChaseInfo>
              {documentRequest.documentsUploaded &&
              documentRequest.documentsUploaded.length > 0 ? (
                <DocChaseInfoItem>
                  <AttachedDocumentIconWrapper>
                    <FontAwesomeIcon icon={faFile} size="1x" />
                    <StyledCheckmarkIcon icon={faCheckCircle} color="#6ABD40" size="xs" />
                  </AttachedDocumentIconWrapper>
                  {documentRequest.documentsUploaded.length}{' '}
                  {documentRequest.documentsUploaded.length > 1
                    ? 'documents'
                    : 'document'}{' '}
                  uploaded
                </DocChaseInfoItem>
              ) : (
                <DocChaseInfoItem>
                  <AttachedDocumentIconWrapper>
                    <FontAwesomeIcon icon={faFile} size="1x" />
                    <StyledCheckmarkIcon icon={faCircleXmark} color="#E02020" size="xs" />
                  </AttachedDocumentIconWrapper>
                  No documents uploaded
                </DocChaseInfoItem>
              )}
              <DocChaseInfoItem>
                <FontAwesomeIcon
                  color={documentRequest.additionalContext ? '#2F80ED' : '#E02020'}
                  icon={faCommentDots}
                  size="1x"
                />
                {documentRequest.additionalContext ? 'Comment added' : 'No comment added'}
              </DocChaseInfoItem>
            </DocChaseInfo>
          )}

        <CreatedDateWrapper>
          Created{' '}
          {toDateLabel({
            date: parseDate(documentRequest.createdAt),
            dateFormat: 'dd/MM/yyyy HH:mm',
          })}
        </CreatedDateWrapper>
      </CollapsedContainer>

      {isExpanded && (
        <ExpandedContainer>
          {documentRequest.completed &&
            (hasUploadedDocuments || documentRequest.additionalContext) && (
              <DocumentAndAdditionalContextWrapper>
                {documentRequest.documentsUploaded ? (
                  <AdditionalContextHeadingWrapper>
                    <H6>Uploaded documents</H6>
                    <CreatedDateWrapper>
                      Submitted on{' '}
                      {toDateLabel({
                        date: parseDate(documentRequest.updatedAt),
                        dateFormat: 'dd/MM/yyyy HH:mm',
                      })}
                    </CreatedDateWrapper>
                  </AdditionalContextHeadingWrapper>
                ) : (
                  <AdditionalContextHeadingWrapper>
                    <H6>No document uploaded</H6>
                  </AdditionalContextHeadingWrapper>
                )}
                <DocumentInfoWrapper>
                  {documentRequest.documentsUploaded?.map((document, index) => (
                    <>
                      <DocumentInfo>
                        <div>{`${documentRequest.documentType} ${index + 1}`}</div>
                        <TertiaryAction onClick={() => handleOpenDocument(index)}>
                          View document
                        </TertiaryAction>
                      </DocumentInfo>{' '}
                      <Divider />
                    </>
                  ))}
                </DocumentInfoWrapper>
                {documentRequest.additionalContext && (
                  <CommentWrapper>
                    <CommentHeading>
                      <CommentIconWrapper>
                        <FontAwesomeIcon color="#FFFFFF" icon={faCommentDots} size="1x" />
                      </CommentIconWrapper>
                      <H6>Comment by {subjectName}</H6>
                    </CommentHeading>
                    <CommentContent>{documentRequest.additionalContext}</CommentContent>
                  </CommentWrapper>
                )}
              </DocumentAndAdditionalContextWrapper>
            )}
          <ExpandedDocumentRequest
            customerId={customerId}
            applicationId={applicationId}
            subjectId={subjectId}
            documentRequest={documentRequest}
          />
        </ExpandedContainer>
      )}
    </Container>
  )
}

export default DocumentRequest
